import axios from 'axios';
import { toast } from 'react-toastify';

// 🔗 API URL based on environment
const isDev = window.location.hostname === 'localhost';
const API_URL = isDev
  ? 'http://localhost:5001/api'   // Local backend for development
  : 'https://markazsync.com/api'; // Production backend

// ✅ Global response interceptor to catch token/session expiration or server issues
let sessionExpiredHandled = false;

axios.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status;
    const message = error.response?.data?.error || '';
    const isNetworkError = error.message === 'Network Error';
    const isLoginRequest = error.config?.url?.includes('/auth/login');

    // ✅ SESSION EXPIRED
    if (
      !sessionExpiredHandled &&
      !isLoginRequest && // ⛔ Skip handling for login
      (status === 401 || message.includes('jwt expired') || message.includes('Invalid token'))
    ) {
      sessionExpiredHandled = true;
      toast.error('Your session expired. Please log in again.');
      localStorage.clear();
      delete axios.defaults.headers.common['Authorization'];

      window.location.href = '/login';
    }

    // ✅ SERVER DOWN
    else if (isNetworkError || !error.response) {
      toast.error('⚠️ Cannot connect to the server. Redirecting...');
      setTimeout(() => {
        window.location.href = '/error';
      }, 1000);
    }

    // ✅ SERVER ERROR (500+)
    else if (status >= 500) {
      toast.error('🚨 A server error occurred. Redirecting...');
      setTimeout(() => {
        window.location.href = '/error';
      }, 1000);
    }

    return Promise.reject(error);
  }
);


// ✅ Register new user
const register = (name, email, password) => {
  return axios.post(`${API_URL}/register`, { name, email, password });
};

// ✅ Login user and store auth data
const login = async (email, password) => {
  try {
    console.log('🛠 Sending Login Request:', { email, password });

    const response = await axios.post(`${API_URL}/auth/login`, { email, password }, {
      withCredentials: true, // Required for cookie-based auth
    });

    console.log('✅ Backend Response:', JSON.stringify(response.data, null, 2));

    const { token, role, id, name, password_changed, markaz_id, markaz_name, financials_enabled } = response.data;

    if (token && role && id) {
      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('role', role);
      localStorage.setItem('id', id);
      localStorage.setItem('name', name || '');
      localStorage.setItem('password_changed', password_changed);
      localStorage.setItem('markaz_id', markaz_id);
      localStorage.setItem('markaz_name', markaz_name || '');
      localStorage.setItem('financials_enabled', financials_enabled ? 'true' : 'false');

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return response.data;
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (error) {
    console.error('❌ Login error:', error.response?.data || error);
    throw error;
  }
};

// ✅ Logout user and clear auth state
const logout = () => {
  ['token', 'role', 'id', 'name', 'password_changed', 'markaz_id', 'markaz_name'].forEach(key =>
    localStorage.removeItem(key)
  );
  localStorage.removeItem('financials_enabled');
  delete axios.defaults.headers.common['Authorization'];
  console.log('✅ User logged out');
};

// ✅ Get current user info if token is valid
const getCurrentUser = () => {
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const id = localStorage.getItem('id');
  const name = localStorage.getItem('name');
  const password_changed = localStorage.getItem('password_changed');
  const markaz_id = localStorage.getItem('markaz_id');
  const markaz_name = localStorage.getItem('markaz_name');
  const financials_enabled = localStorage.getItem('financials_enabled');

  if (!token || !role || !id) return null;

  try {
    const decoded = JSON.parse(atob(token.split('.')[1]));
    if (decoded.exp * 1000 < Date.now()) {
      console.warn('⚠️ Token expired, logging out...');
      logout();
      return null;
    }
  } catch (err) {
    console.error('⚠️ Invalid token structure:', err);
    logout();
    return null;
  }

  return {
    token,
    role,
    id,
    name,
    password_changed,
    markaz_id,
    markaz_name,
    financials_enabled: financials_enabled === 'true',
  };
};

const authService = {
  getCurrentUser,
  login,
  register,
  logout,
};

export default authService;
